import {
  Content,
  Root as RadixTooltip,
  Arrow as RadixTooltipArrow,
  Provider as RadixTooltipProvider,
  Trigger as RadixTooltipTrigger,
  Portal as TooltipPortal,
} from '@radix-ui/react-tooltip';
import * as React from 'react';
import { flushSync } from 'react-dom';

import { cn } from '@heidi/utils';

import { tooltipArrowClasses, tooltipContentClasses } from './tooltip.styles';
import type {
  TooltipArrowProps,
  TooltipCollisionPadding,
  TooltipContentProps,
  TooltipProps,
  TooltipProviderProps,
} from './tooltip.types';

/** The shared hover delay; override only with a stated reason. */
const TOOLTIP_DELAY_DURATION = 350;

const TooltipProvider = ({
  delayDuration = TOOLTIP_DELAY_DURATION,
  ...props
}: TooltipProviderProps) => (
  <RadixTooltipProvider delayDuration={delayDuration} {...props} />
);
TooltipProvider.displayName = 'TooltipProvider';

type TooltipRegistry = {
  registerContent: () => () => void;
  isHeldClosed: boolean;
  replayOpen: () => void;
};

const TooltipRegistryContext = React.createContext<TooltipRegistry | null>(null);

/**
 * Drives Radix's open state itself. A root that opens with nothing to show, or that
 * the caller holds closed with `open={false}`, still has to close through Radix, or
 * the provider keeps skipping the next tooltip's delay.
 */
const Tooltip = ({
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  disableHoverableContent,
  ...props
}: TooltipProps) => {
  const [contentCount, setContentCount] = React.useState(0);
  const [rootOpen, setRootOpen] = React.useState(open === undefined && defaultOpen);
  const rootOpenRef = React.useRef(rootOpen);
  const registerContent = React.useCallback(() => {
    setContentCount((count) => count + 1);
    return () => setContentCount((count) => count - 1);
  }, []);
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      rootOpenRef.current = next;
      // Radix compares its next change against the rendered value, so a close that
      // lands before a re-render would be dropped.
      flushSync(() => setRootOpen(next));
      onOpenChange?.(next);
    },
    [onOpenChange]
  );
  const isHeldClosed = open === false;
  const replayOpen = React.useCallback(() => {
    if (isHeldClosed && rootOpenRef.current) onOpenChange?.(true);
  }, [isHeldClosed, onOpenChange]);
  const registry = React.useMemo(
    () => ({ registerContent, isHeldClosed, replayOpen }),
    [registerContent, isHeldClosed, replayOpen]
  );
  return (
    <RadixTooltip
      {...props}
      // Held closed, Radix still tracks the hover so the close is reported; content stays hidden.
      open={open === true || rootOpen}
      onOpenChange={handleOpenChange}
      disableHoverableContent={
        isHeldClosed || (disableHoverableContent ?? (contentCount === 0 || undefined))
      }
    >
      <TooltipRegistryContext.Provider value={registry}>
        {children}
      </TooltipRegistryContext.Provider>
    </RadixTooltip>
  );
};
Tooltip.displayName = 'Tooltip';

/** Radix ignores a new hover or focus while it already counts the root as open, so a
 * caller holding the tooltip closed would never hear it; this passes each one on. */
const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof RadixTooltipTrigger>,
  React.ComponentPropsWithoutRef<typeof RadixTooltipTrigger>
>(({ onPointerMove, onPointerLeave, onFocus, ...props }, ref) => {
  const registry = React.useContext(TooltipRegistryContext);
  const hasEnteredRef = React.useRef(false);
  // Radix counts a held-closed root as open, so restore what the user actually sees.
  const heldClosedProps = registry?.isHeldClosed
    ? { 'aria-describedby': props['aria-describedby'], 'data-state': 'closed' }
    : {};
  return (
    <RadixTooltipTrigger
      ref={ref}
      {...props}
      {...heldClosedProps}
      onPointerMove={(event) => {
        onPointerMove?.(event);
        if (
          event.defaultPrevented ||
          event.pointerType === 'touch' ||
          hasEnteredRef.current
        )
          return;
        hasEnteredRef.current = true;
        registry?.replayOpen();
      }}
      onPointerLeave={(event) => {
        onPointerLeave?.(event);
        hasEnteredRef.current = false;
      }}
      onFocus={(event) => {
        onFocus?.(event);
        if (!event.defaultPrevented) registry?.replayOpen();
      }}
    />
  );
});
TooltipTrigger.displayName = RadixTooltipTrigger.displayName;

// In the desktop app a fixed title bar overlays the top of the webview and
// paints above tooltips (`TauriAppHeader`, `z-[99999]`; on macOS the native
// traffic-light strip occupies the same 32px). Radix's collision detection
// uses the raw webview viewport, so without an offset top-side tooltips render
// into that strip and get hidden behind it. The bar height arrives as the
// `--app-titlebar-height` var on `<html>` (published by the app's
// `TauriTitlebarHeightVar`, absent — i.e. 0 — on web and in auxiliary Tauri
// windows), keeping this package platform-agnostic: inject it as a top
// `collisionPadding` so Radix flips the tooltip below (or shifts it down for
// left/right placement) when a top-side render would land inside the strip.
const getTitlebarInset = (): number => {
  if (typeof document === 'undefined') return 0;
  // Absent during SSR and while a document is being torn down or replaced —
  // no root element means no published var, which is the same as no titlebar.
  const root = document.documentElement;
  if (!root) return 0;
  // Inline-style read on purpose: `TauriTitlebarHeightVar` publishes the var
  // as an inline style on <html>, and reading `el.style` — unlike
  // `getComputedStyle`, which forces a synchronous style/layout flush — is a
  // plain attribute read. This runs on every TooltipContent render (open +
  // reposition), so it must stay flush-free.
  return Number.parseFloat(root.style.getPropertyValue('--app-titlebar-height')) || 0;
};

const withTitlebarInset = (
  callerValue: TooltipCollisionPadding
): TooltipCollisionPadding => {
  const topInset = getTitlebarInset();
  if (topInset === 0) return callerValue;
  if (callerValue == null) return { top: topInset };
  if (typeof callerValue === 'number') {
    return {
      top: Math.max(callerValue, topInset),
      right: callerValue,
      bottom: callerValue,
      left: callerValue,
    };
  }
  return {
    ...callerValue,
    top: Math.max(callerValue.top ?? 0, topInset),
  };
};

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, collisionPadding, ...props }, ref) => {
  const registry = React.useContext(TooltipRegistryContext);
  const registerContent = registry?.registerContent;
  React.useEffect(() => registerContent?.(), [registerContent]);
  if (registry?.isHeldClosed) return null;
  return (
    <TooltipPortal>
      <Content
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={withTitlebarInset(collisionPadding)}
        className={cn(tooltipContentClasses, className)}
        {...props}
      />
    </TooltipPortal>
  );
});
TooltipContent.displayName = Content.displayName;

const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof RadixTooltipArrow>,
  TooltipArrowProps
>(({ className, ...props }, ref) => (
  <RadixTooltipArrow
    ref={ref}
    className={cn(tooltipArrowClasses, className)}
    {...props}
  />
));
TooltipArrow.displayName = RadixTooltipArrow.displayName;

export {
  Tooltip,
  TOOLTIP_DELAY_DURATION,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
};
